import { useEffect, useRef, useState } from 'react';
import { Mic, Square, Volume2 } from 'lucide-react';
import { prepareCantonesePlayback, recognizeCantonese, speakCantoneseQueue, supportsBrowserRecognition, supportsCantonesePlayback, supportsCloudPlayback, type CantoneseSpeechItem } from './browserVoice';
import { assertDiscussionAudio, assertDiscussionCaptureCapacity, discussionCaptureGeneration, flushDiscussionCaptures, pendingDiscussionCaptures, retryDiscussionCaptures, saveDiscussionCapture, subscribeDiscussionCaptures, type DiscussionCapture, type DiscussionDraftMetadata } from './discussionCapture';

type Phase = 'idle' | 'preparing' | 'listening' | 'saving' | 'thinking' | 'speaking' | 'error';
type VoiceReply = CantoneseSpeechItem;
type PartnerInterruption = { id: string; nameZh: string; text: string };
type Props = {
  sessionId: string;
  mode?: 'live' | 'demo';
  disabled?: boolean;
  startDisabled?: boolean;
  cloudPlayback?: boolean;
  openingTurn?: VoiceReply;
  interruptingPartners?: Array<{ id: string; nameZh: string }>;
  learnerTurnCount?: number;
  lastInterruptedLearnerTurnCount?: number;
  onTurn: (text: string, options?: { requestId?: string; interruptedByAvatarId?: string }) => Promise<VoiceReply[]>;
  onRecording?: (capture: DiscussionCapture) => Promise<void>;
  onController?: (control: { stopAndSave: () => Promise<void> } | null) => void;
  onActiveChange: (active: boolean) => void;
  onSpeakerChange: (speakerId: string | null) => void;
  onDraft: (text: string, metadata?: DiscussionDraftMetadata) => void;
};

/** Leave a short quiet gap after speech, before asking the microphone to listen. */
function quietGap(signal: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) { reject(new DOMException('Stopped', 'AbortError')); return; }
    const timer = window.setTimeout(() => { signal.removeEventListener('abort', abort); resolve(); }, 300);
    const abort = () => { window.clearTimeout(timer); reject(new DOMException('Stopped', 'AbortError')); };
    signal.addEventListener('abort', abort, { once: true });
  });
}

export default function VoiceConversation(props: Props) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [active, setActive] = useState(false);
  const [canTakeTurn, setCanTakeTurn] = useState(false);
  const [, captureUpdate] = useState(0);
  const [recordingMessage, setRecordingMessage] = useState('');
  const [preview, setPreview] = useState('');
  const [message, setMessage] = useState('按開始後，辨認到嘅說話會自動送出；伙伴講完就會再聆聽。');
  const [support, setSupport] = useState(() => ({ recognition: supportsBrowserRecognition(), playback: props.cloudPlayback ? supportsCloudPlayback() : supportsCantonesePlayback() }));
  const controller = useRef<AbortController | null>(null);
  const completion = useRef<Promise<void> | null>(null);
  const playback = useRef<AbortController | null>(null);
  const recognition = useRef<AbortController | null>(null);
  const interruptionTimer = useRef<{ owner: AbortController; id: number } | null>(null);
  const learnerTurns = useRef(props.learnerTurnCount ?? 0);
  const lastInterrupted = useRef<number | null>(props.lastInterruptedLearnerTurnCount ?? null);
  const interruptionSequence = useRef(0);
  const speakingPartner = useRef<string | null>(null);
  const refusedYield = useRef(false), yieldAfterSentence = useRef(false);
  const attemptedOpening = useRef<{ sessionId: string; turnId: string } | null>(null);
  const mounted = useRef(false);
  const callbacks = useRef(props);
  callbacks.current = props;

  function clearInterruptionTimer(owner?: AbortController) {
    const timer = interruptionTimer.current;
    if (timer && (!owner || timer.owner === owner)) { window.clearTimeout(timer.id); interruptionTimer.current = null; }
  }
  function clearFloor() { speakingPartner.current = null; refusedYield.current = false; yieldAfterSentence.current = false; }

  async function stopAndSave(note = '語音對話已停止。你可以繼續用文字回應。Voice conversation stopped.') {
    const scope = callbacks.current.sessionId, recordingEnabled = Boolean(callbacks.current.onRecording);
    const previous = controller.current;
    controller.current = null;
    previous?.abort();
    recognition.current?.abort(); recognition.current = null; clearInterruptionTimer();
    playback.current?.abort(); playback.current = null;
    clearFloor();
    callbacks.current.onSpeakerChange(null);
    if (recordingEnabled) {
      if (mounted.current) { setCanTakeTurn(false); setPhase('saving'); setMessage('正在保存已收取嘅錄音。Saving captured audio…'); }
      await completion.current;
      try { await flushDiscussionCaptures(scope); }
      catch (problem) { if (mounted.current) { setActive(true); setPhase('error'); setMessage(problem instanceof Error ? problem.message : '錄音未保存，請重試。Recording save failed.'); } throw problem; }
    }
    callbacks.current.onActiveChange(false);
    if (mounted.current) { setActive(false); setCanTakeTurn(false); setPhase('idle'); setMessage(note); }
  }
  function stop(note?: string) { void stopAndSave(note).catch(() => undefined); }
  async function retryRecording() {
    if (callbacks.current.disabled) return;
    setPhase('saving'); setRecordingMessage('正在重試保存錄音。Retrying recording save…');
    try { await retryDiscussionCaptures(callbacks.current.sessionId); if (mounted.current) { setActive(false); setPhase('idle'); setRecordingMessage('錄音已保存；請檢查文字後發送。Recording saved; check the draft before sending.'); callbacks.current.onActiveChange(false); } }
    catch (problem) { if (mounted.current) { setPhase('error'); setMessage(problem instanceof Error ? problem.message : 'Recording save failed.'); } }
  }

  function takeTurn() {
    const current = playback.current;
    if (!current || current.signal.aborted) return;
    const partner = callbacks.current.interruptingPartners?.find(item => item.id === speakingPartner.current);
    if (partner && !refusedYield.current) {
      refusedYield.current = true; yieldAfterSentence.current = true;
      setMessage(`${partner.nameZh}想先講埋呢句；再按一次「我想講」就會即時輪到你。Partner finishing this sentence; press My turn again to speak now.`);
      return;
    }
    yieldAfterSentence.current = false;
    current.abort('learner-takes-turn');
    setCanTakeTurn(false);
    setMessage('好，輪到你；等伙伴聲音停低就會開咪。Your turn; listening starts after playback stops.');
  }

  useEffect(() => {
    mounted.current = true;
    attemptedOpening.current = null;
    learnerTurns.current = callbacks.current.learnerTurnCount ?? 0;
    lastInterrupted.current = callbacks.current.lastInterruptedLearnerTurnCount ?? null;
    interruptionSequence.current = 0; clearFloor();
    setPhase('idle'); setActive(false); setCanTakeTurn(false); setPreview('');
    return () => {
      mounted.current = false;
      const previous = controller.current; controller.current = null; previous?.abort();
      recognition.current?.abort(); recognition.current = null; clearInterruptionTimer();
      playback.current?.abort(); playback.current = null;
      clearFloor();
      callbacks.current.onActiveChange(false); callbacks.current.onSpeakerChange(null);
    };
  }, [props.sessionId]);
  useEffect(() => subscribeDiscussionCaptures(() => captureUpdate(value => value + 1)), []);
  useEffect(() => { const report = props.onController; report?.({ stopAndSave: () => stopAndSave() }); return () => report?.(null); }, [props.sessionId, props.onController]);

  useEffect(() => {
    learnerTurns.current = Math.max(learnerTurns.current, props.learnerTurnCount ?? 0);
    const recorded = props.lastInterruptedLearnerTurnCount;
    if (recorded !== undefined) lastInterrupted.current = Math.max(lastInterrupted.current ?? -1, recorded);
  }, [props.learnerTurnCount, props.lastInterruptedLearnerTurnCount]);

  useEffect(() => {
    const update = () => setSupport({ recognition: supportsBrowserRecognition(), playback: props.cloudPlayback ? supportsCloudPlayback() : supportsCantonesePlayback() });
    update();
    window.speechSynthesis?.addEventListener?.('voiceschanged', update);
    return () => window.speechSynthesis?.removeEventListener?.('voiceschanged', update);
  }, [props.cloudPlayback]);

  useEffect(() => { if (props.disabled && controller.current) stop('語音已暫停。按開始先會再開咪。Voice conversation paused.'); }, [props.disabled]);

  async function start() {
    if (controller.current || callbacks.current.disabled || callbacks.current.startDisabled) return;
    if (!supportsBrowserRecognition() || !(callbacks.current.cloudPlayback ? supportsCloudPlayback() : supportsCantonesePlayback())) return;
    if (callbacks.current.onRecording) { try { assertDiscussionCaptureCapacity(callbacks.current.sessionId); } catch (problem) { setPhase('error'); setMessage((problem as Error).message); return; } }
    const run = new AbortController();
    const runningSessionId = callbacks.current.sessionId;
    controller.current = run;
    const uploadRecording = callbacks.current.onRecording;
    const recordingGeneration = discussionCaptureGeneration();
    let finishRun!: () => void;
    const finished = new Promise<void>(resolve => { finishRun = resolve; }); completion.current = finished;
    const isCurrent = () => mounted.current && controller.current === run && !run.signal.aborted && !callbacks.current.disabled && callbacks.current.sessionId === runningSessionId;
    const play = async (items: VoiceReply[]) => {
      const queue = new AbortController();
      const abort = () => queue.abort();
      run.signal.addEventListener('abort', abort, { once: true });
      if (run.signal.aborted) queue.abort();
      playback.current = queue; clearFloor();
      if (isCurrent()) setCanTakeTurn(true);
      const speaker = (id: string | null) => {
        if (!isCurrent() || playback.current !== queue || queue.signal.aborted) return;
        if (id !== speakingPartner.current) { speakingPartner.current = id; refusedYield.current = false; }
        callbacks.current.onSpeakerChange(id);
        if (id === null && yieldAfterSentence.current) {
          // The owned media has ended and released its source. Drop remaining
          // queue items before another partner can start, then give the floor back.
          yieldAfterSentence.current = false;
          queue.abort('learner-takes-turn'); setCanTakeTurn(false);
          setMessage('好，輪到你；等伙伴聲音停低就會開咪。Your turn; listening starts after playback stops.');
        }
      };
      try { await speakCantoneseQueue(items, { signal: queue.signal, onSpeaker: speaker }); }
      catch (problem) {
        // Yielding cancels this speech queue, then the same conversation waits
        // for fresh learner speech. It never starts another provider round.
        if (!isCurrent() || queue.signal.reason !== 'learner-takes-turn' || !((problem instanceof Error || problem instanceof DOMException) && problem.name === 'AbortError')) throw problem;
      } finally {
        run.signal.removeEventListener('abort', abort);
        if (playback.current === queue) { playback.current = null; clearFloor(); if (isCurrent()) callbacks.current.onSpeakerChange(null); if (mounted.current) setCanTakeTurn(false); }
      }
    };
    let pendingSpeech = '', lastCapture: DiscussionCapture | null = null;
    const draft = (text: string) => { if (lastCapture) callbacks.current.onDraft(text, { requestId: lastCapture.id, transcript: lastCapture.transcript ?? '' }); else callbacks.current.onDraft(text); };
    setActive(true); setPreview(''); callbacks.current.onActiveChange(true);
    try {
      if (callbacks.current.cloudPlayback) {
        setPhase('speaking'); setMessage('準備伙伴聲音，稍後就輪到你。Preparing partner audio…');
        await prepareCantonesePlayback({ signal: run.signal });
        if (!isCurrent()) return;
      }
      const opener = callbacks.current.openingTurn;
      if (opener && attemptedOpening.current?.sessionId !== runningSessionId) {
        // Starting voice again resumes listening. Replay is an explicit action;
        // a stopped or failed opening must not restart the same speech loop.
        attemptedOpening.current = { sessionId: runningSessionId, turnId: opener.id };
        setPhase('speaking'); setMessage('先聽伙伴嘅開場，講完就輪到你。Partner speaking…');
        await play([opener]);
        if (!isCurrent()) return;
        await quietGap(run.signal);
      }
      while (isCurrent()) {
        pendingSpeech = ''; lastCapture = null;
        setPhase('preparing'); setPreview(''); setMessage('準備咪高峰，連接成功後先開始講。Connecting microphone…');
        const utteranceIndex = Math.max(learnerTurns.current, callbacks.current.learnerTurnCount ?? 0);
        const partners = callbacks.current.interruptingPartners ?? [];
        const previousInterruption = Math.max(lastInterrupted.current ?? -1, callbacks.current.lastInterruptedLearnerTurnCount ?? -1);
        const mayInterrupt = callbacks.current.mode === 'live' && partners.length > 0 && utteranceIndex >= 1 && (previousInterruption < 0 || utteranceIndex - previousInterruption >= 3);
        const capture = mayInterrupt ? new AbortController() : run;
        const abortCapture = () => capture.abort();
        if (capture !== run) run.signal.addEventListener('abort', abortCapture, { once: true });
        recognition.current = capture;
        let listeningAt: number | null = null, interrupted: PartnerInterruption | null = null;
        const captureCurrent = () => isCurrent() && recognition.current === capture && !capture.signal.aborted;
        const interruptIfReady = () => {
          if (!mayInterrupt || !captureCurrent() || listeningAt === null || Date.now() - listeningAt < 4000 || (pendingSpeech.match(/[\u3400-\u9fff]/gu)?.length ?? 0) < 16) return;
          const partner = partners[interruptionSequence.current % partners.length]; interruptionSequence.current += 1;
          interrupted = { ...partner, text: pendingSpeech.trim() }; lastInterrupted.current = utteranceIndex;
          clearInterruptionTimer(capture);
          setPhase('thinking'); setMessage(`正在暫停收音，${partner.nameZh}想插句回應。Partner interrupting; stopping the microphone before replying.`);
          capture.abort('partner-interruption');
        };
        let recognized: string;
        try {
          recognized = await recognizeCantonese({ signal: capture.signal,
            ...(uploadRecording ? { recordingSource: 'voice' as const, onRecording: async (recording: DiscussionCapture) => {
              if (recording.blob.size <= 44) { if (mounted.current && callbacks.current.sessionId === runningSessionId) setRecordingMessage('未捕捉到音訊樣本，呢次冇錄音。No audio samples captured.'); assertDiscussionAudio(recording); }
              lastCapture = recording;
              if (mounted.current && callbacks.current.sessionId === runningSessionId) { setPhase('saving'); setRecordingMessage('咪高峰已關閉，正在保存錄音。Microphone off; saving recording…'); if (recording.transcript) draft(recording.transcript); }
              await saveDiscussionCapture(runningSessionId, recording, uploadRecording, recordingGeneration);
              if (mounted.current && callbacks.current.sessionId === runningSessionId) setRecordingMessage('錄音已保存。Recording saved.');
            } } : {}),
            onListening: () => { if (captureCurrent()) { if (listeningAt === null) { listeningAt = Date.now(); if (mayInterrupt) interruptionTimer.current = { owner: capture, id: window.setTimeout(interruptIfReady, 4000) }; } setPhase('listening'); setMessage('聆聽中，請講廣東話；停頓後會自動送出。Listening in Cantonese…'); } },
            onInterim: text => { if (captureCurrent()) { pendingSpeech = text; setPreview(text); callbacks.current.onDraft(text); interruptIfReady(); } },
          });
        } catch (problem) {
          if (!isCurrent() || capture.signal.reason !== 'partner-interruption' || !interrupted || !((problem instanceof Error || problem instanceof DOMException) && problem.name === 'AbortError')) throw problem;
          recognized = (interrupted as PartnerInterruption).text;
        } finally {
          clearInterruptionTimer(capture); if (capture !== run) run.signal.removeEventListener('abort', abortCapture);
          if (recognition.current === capture) recognition.current = null;
        }
        if (!isCurrent()) return;
        // Keep the exact partial captured when the microphone was interrupted;
        // late final recognition must not replace the submitted half-sentence.
        const interruption = interrupted as PartnerInterruption | null;
        const text = (interruption?.text ?? recognized).trim();
        if (!text) throw new Error('未辨認到說話。請再開始，或者輸入文字。No speech recognised; restart or type instead.');
        pendingSpeech = text; setPreview(text); draft(text);
        setPhase('thinking'); setMessage(interruption ? `已暫停收音，${interruption.nameZh}想插句回應。Partner interrupting; microphone stopped before replying.` : '已送出說話，等緊伙伴回應。Waiting for partners…');
        const options = { ...(lastCapture ? { requestId: (lastCapture as DiscussionCapture).id } : {}), ...(interruption ? { interruptedByAvatarId: interruption.id } : {}) };
        const replies = Object.keys(options).length ? await callbacks.current.onTurn(text, options) : await callbacks.current.onTurn(text);
        learnerTurns.current = Math.max(learnerTurns.current, utteranceIndex + 1, callbacks.current.learnerTurnCount ?? 0);
        pendingSpeech = '';
        if (!isCurrent()) return;
        if (!replies.length) throw new Error('未有可播放嘅伙伴回應。請檢查對話紀錄，再重新開始。No partner response to play; check the conversation.');
        setPhase('speaking'); setMessage('播完呢輪回應，就會再聆聽你嘅意見。Partners speaking…');
        await play(replies);
        if (!isCurrent()) return;
        await quietGap(run.signal);
      }
    } catch (caught) {
      if (!isCurrent()) return;
      const problem = caught instanceof Error ? caught.message : '語音對話未能繼續。請重試，或者用文字回應。Voice conversation stopped.';
      if (pendingSpeech.trim()) draft(pendingSpeech.trim());
      controller.current = null; run.abort();
      recognition.current?.abort(); recognition.current = null; clearInterruptionTimer();
      playback.current?.abort(); playback.current = null;
      clearFloor();
      const unsaved = pendingDiscussionCaptures(runningSessionId).length > 0;
      callbacks.current.onActiveChange(unsaved); callbacks.current.onSpeakerChange(null);
      setActive(unsaved); setCanTakeTurn(false); setPhase('error'); setMessage(problem);
    } finally { finishRun(); if (completion.current === finished) completion.current = null; }
  }

  const available = support.recognition && support.playback;
  const pendingRecordings = pendingDiscussionCaptures(props.sessionId);
  return <section className="cnt-voice-conversation" aria-label="語音對話 Voice conversation">
    <div className="cnt-voice-heading"><div><h3><Mic size={18} /> 直接講，接住傾。<small>Voice conversation</small></h3><p>瀏覽器辨認廣東話；{props.cloudPlayback ? 'Alibaba 合成伙伴嘅粵語回應。' : '伙伴使用裝置提供嘅粵語聲音。'}{props.mode === 'demo' ? '呢場練習嘅伙伴使用預設示範對話。' : 'AI 伙伴按對話文字回應。'}</p></div></div>
    <p className="cnt-voice-consent">啟動後，辨認到嘅文字會自動送出。咪高峰語音由瀏覽器語音服務處理；{props.cloudPlayback && 'Alibaba 語音服務只會收到伙伴回應文字以產生聲音；'}可隨時停止開咪同播放。{props.cloudPlayback && 'Cloud speech receives partner reply text; microphone recognition remains with the browser.'}</p>
    {props.mode === 'live' && Boolean(props.interruptingPartners?.length) && <p className="cnt-voice-consent">今次有會插話嘅伙伴：插話時會先暫停收音，再送出已辨認嘅半句；伙伴講完先再開咪。Partner interruptions pause capture and submit the recognised partial turn; the microphone resumes after playback.</p>}
    {!available && !active ? <p className="cnt-voice-fallback" role="status">{!support.recognition ? '呢個瀏覽器未支援連續語音對話。' : '裝置未提供粵語播放聲音。'}你仍然可以喺下面輸入文字，或者用「用語音填入」檢查後再發送。Browser voice conversation unavailable; use text or Dictate below.</p> : <>
      <div className="cnt-voice-actions">{active ? <button type="button" className="secondary-button" onClick={() => stop()}><Square size={15} /> 停止語音對話 Stop voice conversation</button> : <button type="button" className="primary-button" disabled={props.disabled || props.startDisabled || pendingRecordings.length > 0} onClick={() => void start()}><Mic size={17} /> 開始語音對話 Start voice conversation</button>}{active && canTakeTurn && <button type="button" className="secondary-button" onClick={takeTurn}><Mic size={15} /> 我想講 My turn</button>}<span className={`cnt-voice-phase cnt-voice-${phase}`} role="status">{active && (phase === 'speaking' ? <Volume2 size={16} /> : <span className="cnt-dot" />)}{phase === 'preparing' ? '準備咪高峰 Connecting microphone' : phase === 'listening' ? '聆聽中 Listening' : phase === 'saving' ? '保存錄音 Saving recording' : phase === 'thinking' ? '等待回應 Thinking' : phase === 'speaking' ? '伙伴發言 Speaking' : phase === 'error' ? '已停止 Stopped' : '咪高峰已關閉 Mic off'}</span></div>
      <p className="cnt-voice-message" role={phase === 'error' ? 'alert' : undefined}>{message}</p>
      {props.onRecording && <p className="cnt-voice-consent" role="status">{phase === 'listening' ? '正在保存呢段咪高峰錄音，停止收音後會上載。Recording this microphone interval; upload starts when capture stops.' : recordingMessage || '語音對話會保存每段收音，包括未辨認到文字或被打斷嘅錄音。Microphone intervals are saved, including unrecognised or interrupted speech.'}</p>}
      {pendingRecordings.length > 0 && <button type="button" className="secondary-button" disabled={props.disabled || phase === 'saving'} onClick={() => void retryRecording()}>重試保存錄音 Retry recording upload</button>}
      {preview && <div className="cnt-voice-preview"><small>{phase === 'listening' ? '辨認中 · Recognising' : '辨認文字 · Recognised speech'}</small><p>{preview}</p></div>}
      {phase === 'error' && <p className="cnt-voice-retry-note">語音已停止；唔會自動重試。已辨認嘅文字會保留喺下方，可檢查後發送。Voice is stopped; check the retained text before retrying.</p>}
    </>}
  </section>;
}
